import type { ApolloError, ServerError, ServerParseError } from '@apollo/client';
import { useTelemetry } from '@tbitop/telemetry';
import { useNotifications } from '@tbitop/ui';
import type { GraphQLFormattedError } from 'graphql';
import { debounce, isNull, pick } from 'lodash-es';
import { useMemo } from 'react';
import { log } from './log';

export const useGlobalGraphQLErrorHandler = () => {
  const notify = useNotifications();
  const telemetry = useTelemetry();

  return useMemo(() => {
    const handleGraphQlError = (errors: readonly GraphQLFormattedError[]) => {
      for (const error of errors) {
        notify.error('Aufgrund eines technischen Problems konnte diese Aktion nicht durchgeführt werden.');
        log.error(`[GraphQL_Error]: ${error.message}`);
        telemetry?.trackException(new Error(error.message), pick(error, 'graphQLErrors', 'clientErrors', 'networkError'));
      }
    };

    const handleNetworkError = (error: Error | ServerError | ServerParseError | null) => {
      notify.error('Aufgrund eines Verbindungsfehlers konnte diese Aktion nicht durchgeführt werden.');
      log.error(`[GraphQL_Warning_Network]: ${error?.message ?? 'null'}`);
      if (!isNull(error)) {
        telemetry?.trackException(error, pick(error, 'graphQLErrors', 'clientErrors', 'networkError'));
      }
    };

    return {
      onGraphQlError: debounce(handleGraphQlError, 1000),
      onNetworkError: debounce(handleNetworkError, 1000),
    };
  }, [notify, telemetry]);
};

export const useGraphQLErrorHandler = () => {
  const notify = useNotifications();
  const telemetry = useTelemetry();

  return useMemo(() => {
    const handleWithNotification = (notificationType: 'error' | 'warning' | 'info') => (notificationMessage: string) => (error: ApolloError) => {
      log.error(error);
      notify[notificationType](notificationMessage);
      telemetry?.trackException(error, pick(error, 'graphQLErrors', 'clientErrors', 'networkError'));
    };

    return {
      handleWithErrorNotification: handleWithNotification('error'),
      handleWithWarningNotification: handleWithNotification('warning'),
      handleWithInfoNotification: handleWithNotification('warning'),
    };
  }, [notify, telemetry]);
};
